/**
 * `/s/:token` — the read-only view of a shared flight. No account needed; the
 * person you fly with opens it, reads it and prints the PDF, and changes
 * nothing.
 *
 * It has no rail — a share link has no account behind it — and it **never
 * fetches**. The freshness chips render from the stored `plans.meta` with their
 * own timestamps and no ⟳: the reader is the one person who cannot re-derive
 * these numbers, so when they were read matters more here than anywhere else and
 * must not look like a button.
 */
import { useMemo, useState } from 'react';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import * as api from '../api';
import { Brand } from '../app/Brand';
import { Inspector } from '../app/Inspector';
import { SummaryStrip } from '../app/SummaryStrip';
import { PaneSheet } from '../app/PaneSheet';
import { TabBar, type TabBarItem } from '../app/TabBar';
import { TABS, tabOf, type TabKey } from '../app/tabs';
import { IconForm, IconMap, IconNavLog, IconTerrain, IconWeather } from '../app/icons';
import { ReadOnlyProvider } from '../components/Field';
import { useT } from '../i18n';
import { RouteMap } from '../map';
import { MapBridgeProvider } from '../map/mapBridge';
import { useNarrow, useViewportHeightVar } from '../media';
import { PlanProvider, usePlanStore } from '../planContext';
import { downloadPdf } from '../pdf';
import { PlanStore } from '../store';
import { WxProvider } from '../wxContext';

/** Everything but Export: there is nothing on that pane a reader may do. */
const SHARED_TABS = TABS.filter(x => x.key !== 'export');

export default function Shared({ token }: { token: string }) {
  const store = useMemo(() => new PlanStore(), [token]);
  return (
    <PlanProvider store={store}>
      <MapBridgeProvider>
        <SharedInner token={token} />
      </MapBridgeProvider>
    </PlanProvider>
  );
}

function SharedInner({ token }: { token: string }) {
  const store = usePlanStore();
  const t = useT();
  const narrow = useNarrow();
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState<unknown>(null);
  const [title, setTitle] = useState('');
  const [pdfBusy, setPdfBusy] = useState(false);
  const [tab, setTab] = useState<TabKey>(tabOf(undefined));
  useViewportHeightVar();

  useEffect(() => {
    let cancelled = false;
    setState('loading');
    void api
      .getSharedPlan(token)
      .then(p => {
        if (cancelled) return;
        // The derived-tag sets are deliberately not loaded here: the reader has no
        // access to the owner's aircraft, so a "from aircraft profile" mark would
        // name a source this page cannot show. `meta.wx`, `meta.winds` and
        // `meta.terrain` ARE kept, because they carry the one thing this page
        // cannot re-derive: WHEN the numbers on it were read.
        const meta =
          p.meta?.wx || p.meta?.winds || p.meta?.terrain
            ? {
                ...(p.meta.wx ? { wx: p.meta.wx } : {}),
                ...(p.meta.winds ? { winds: p.meta.winds } : {}),
                ...(p.meta.terrain ? { terrain: p.meta.terrain } : {}),
              }
            : null;
        store.loadDoc({ values: p.values, manual: p.manual, route: p.route, meta });
        setTitle(p.title);
        setState('ready');
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e);
        setState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [token, store]);

  const onPdf = async () => {
    setPdfBusy(true);
    try {
      await downloadPdf(store.collect());
    } catch (e) {
      window.alert(t('err.pdf', { error: api.apiMessage(t, e) }));
    } finally {
      setPdfBusy(false);
    }
  };

  if (state === 'error') {
    return (
      <div className="ae-centre">
        <div className="ae-centre-brand">
          <Brand size="lg" />
        </div>
        <p className="ae-centre-lead">
          {error instanceof api.ApiError && error.status !== 404
            ? api.apiMessage(t, error)
            : t('shared.invalid')}
        </p>
        <Link className="ae-btn ae-btn-primary" to="/">
          {t('shared.own')}
        </Link>
      </div>
    );
  }

  const paneCtx = { readOnly: true, aircraft: [], aircraftId: null };
  const activeTab = SHARED_TABS.find(x => x.key === tab) ?? SHARED_TABS[0];

  /* a phone has no inspector: the tab bar raises the pane over the map, and the
     Formular is one of the four, because that is what a shared plan is read for */
  const tabItems: TabBarItem[] = [
    { key: 'route', label: t('flight.tab.map'), Icon: IconMap, onSelect: () => setTab('route') },
    { key: 'navlog', label: t('insp.navlog'), Icon: IconNavLog, onSelect: () => setTab('navlog') },
    { key: 'weather', label: t('insp.weather'), Icon: IconWeather, onSelect: () => setTab('weather') },
    { key: 'terrain', label: t('insp.terrain'), Icon: IconTerrain, onSelect: () => setTab('terrain') },
    { key: 'form', label: t('insp.form'), Icon: IconForm, onSelect: () => setTab('form') },
  ];

  return (
    <ReadOnlyProvider value={true}>
      <WxProvider>
        <div className="ae-shell shared">
          <header className="ae-phonehead">
            <Link to="/" className="ae-brand" aria-label="Aether">
              <Brand size="sm" />
            </Link>
            <span className="ae-phonetitle">{title}</span>
            <span className="ae-chip">{t('shared.readonly')}</span>
            <button
              type="button"
              className="ae-btn"
              disabled={pdfBusy || state !== 'ready'}
              onClick={() => void onPdf()}
            >
              {pdfBusy ? t('ed.pdf.busy') : t('ed.pdf')}
            </button>
          </header>
          <SummaryStrip />
          <div className="ae-content fill">
            {state === 'ready' ? (
              narrow ? (
                <div className="ae-workspace phone">
                  <div className="ae-map">
                    <RouteMap readOnly panel={tab === 'route'} />
                  </div>
                  {tab === 'route' ? null : (
                    <PaneSheet title={t(activeTab?.labelKey ?? '')} onClose={() => setTab('route')}>
                      {activeTab?.render(paneCtx)}
                    </PaneSheet>
                  )}
                </div>
              ) : (
                <div className="ae-workspace">
                  <div className="ae-map">
                    <RouteMap readOnly panel={false} />
                  </div>
                  <Inspector tab={tab} onTab={setTab} ctx={paneCtx} tabs={SHARED_TABS} />
                </div>
              )
            ) : (
              <div className="ae-page">{t('loading')}</div>
            )}
          </div>
          {narrow ? <TabBar items={tabItems} active={tab} /> : null}
          <footer className="ae-sharedfoot">
            <Link to="/">{t('shared.madeWith')}</Link>
            {' · '}
            <Link to="/sources">{t('footer.sources')}</Link>
          </footer>
        </div>
      </WxProvider>
    </ReadOnlyProvider>
  );
}
